import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from '../../../lib/toast';
import { normalizeWhatsAppNumber, buildWaMeLink } from '../../../lib/whatsapp';
import { API, auth, money, fmtDate, inputCls } from '../finance/shared';
import { statusMeta } from './bookingStatusMeta';

function CustomerHistory({ phone }) {
  const [bookings, setBookings] = useState(null);

  useEffect(() => {
    axios.get(`${API}/venues/bookings/search`, { ...auth(), params: { search: phone, limit: 20 } })
      .then((res) => setBookings(res.data.items || []))
      .catch(() => setBookings([]));
  }, [phone]);

  if (bookings === null) return <div className="mt-2 h-10 animate-pulse rounded-lg bg-slate-100" />;
  if (bookings.length === 0) return <p className="mt-2 text-xs text-slate-400">لا توجد حجوزات سابقة</p>;

  return (
    <div className="mt-2 space-y-1.5" data-testid={`customer-history-${phone}`}>
      {bookings.map((b) => {
        const meta = statusMeta(b.status);
        return (
          <div key={b.id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-1.5 text-xs">
            <span className="text-slate-600">{b.venue_name} {b.court_label ? `— ${b.court_label}` : ''} • {b.date} • {b.start_time}-{b.end_time}</span>
            <span className={`rounded-full border px-2 py-0.5 font-bold ${meta.badge}`}>{meta.label}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function CustomersTab() {
  const [customers, setCustomers] = useState(null);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [expanded, setExpanded] = useState(null);
  const [editingNotes, setEditingNotes] = useState(null);
  const [noteText, setNoteText] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/venues/owner/customers`, { ...auth(), params: search ? { search } : {} });
      setCustomers(res.data || []);
    } catch { setCustomers([]); }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const sorted = (customers || []).slice().sort((a, b) => (
    sortBy === 'bookings' ? b.bookings_count - a.bookings_count : (b.last_booking_date || '').localeCompare(a.last_booking_date || '')
  ));
  const totalRevenue = (customers || []).reduce((sum, c) => sum + (c.total_amount || 0), 0);

  const saveNote = async (phone) => {
    try {
      await axios.patch(`${API}/venues/owner/customers/${encodeURIComponent(phone)}/notes`, { notes: noteText }, auth());
      toast.success('تم حفظ الملاحظة');
      setEditingNotes(null);
      load();
    } catch { toast.error('فشل حفظ الملاحظة'); }
  };

  return (
    <div className="space-y-4" data-testid="customers-tab">
      {customers !== null && customers.length > 0 && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-2" data-testid="customers-kpi-bar">
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-xs text-slate-400">إجمالي العملاء</p>
            <p className="mt-1 text-xl font-black text-slate-900" data-testid="customers-kpi-total">{customers.length}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-xs text-slate-400">إجمالي الإيراد منهم</p>
            <p className="mt-1 text-xl font-black text-emerald-700" data-testid="customers-kpi-revenue">{money(totalRevenue)}</p>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <input placeholder="بحث بالاسم أو الجوال" value={search} onChange={(e) => setSearch(e.target.value)} className={`${inputCls} max-w-sm`} data-testid="customers-search" />
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className={`${inputCls} w-auto`} data-testid="customers-sort">
          <option value="recent">الأحدث</option>
          <option value="bookings">الأكثر حجزاً</option>
        </select>
      </div>

      {customers === null ? (
        <div className="h-40 animate-pulse rounded-2xl bg-slate-100" />
      ) : customers.length === 0 ? (
        <p className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-400" data-testid="customers-empty">لا يوجد عملاء بعد</p>
      ) : (
        <div className="space-y-2" data-testid="customers-list">
          {sorted.map((c) => {
            const waNum = normalizeWhatsAppNumber(c.phone);
            const isExpanded = expanded === c.phone;
            return (
              <div key={c.phone} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`customer-row-${c.phone}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <button onClick={() => setExpanded(isExpanded ? null : c.phone)} className="text-right" data-testid={`customer-expand-${c.phone}`}>
                    <p className="text-sm font-bold text-slate-900">{c.name || 'بلا اسم'}</p>
                    <p className="text-xs text-slate-500" dir="ltr">{c.phone}</p>
                  </button>
                  <div className="flex gap-4 text-center">
                    <div><p className="text-xs text-slate-400">الحجوزات</p><p className="text-sm font-bold text-slate-800">{c.bookings_count}</p></div>
                    <div><p className="text-xs text-slate-400">آخر حجز</p><p className="text-sm font-bold text-slate-800">{fmtDate(c.last_booking_date)}</p></div>
                    <div><p className="text-xs text-slate-400">الإجمالي</p><p className="text-sm font-bold text-emerald-700">{money(c.total_amount)}</p></div>
                  </div>
                  {waNum && <a href={buildWaMeLink(waNum, `مرحباً ${c.name || ''}، نتشرف بخدمتك في ملعبنا مجدداً!`)} target="_blank" rel="noreferrer" className="rounded-lg border border-emerald-200 px-3 py-1.5 text-xs font-bold text-emerald-700" data-testid={`customer-whatsapp-${c.phone}`}>واتساب</a>}
                </div>
                {isExpanded && <CustomerHistory phone={c.phone} />}
                <div className="mt-2 border-t border-slate-100 pt-2">
                  {editingNotes === c.phone ? (
                    <div className="flex gap-2">
                      <input className={inputCls} value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="ملاحظة عن العميل" data-testid={`customer-note-input-${c.phone}`} />
                      <button onClick={() => saveNote(c.phone)} className="rounded-lg bg-orange-600 px-3 py-1.5 text-xs font-bold text-white" data-testid={`customer-note-save-${c.phone}`}>حفظ</button>
                    </div>
                  ) : (
                    <button onClick={() => { setEditingNotes(c.phone); setNoteText(c.notes || ''); }} className="text-xs text-slate-500 hover:text-orange-600" data-testid={`customer-note-edit-${c.phone}`}>
                      {c.notes ? `ملاحظة: ${c.notes}` : '+ إضافة ملاحظة'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
